// Design tokens for the dashboard renderer, ported from Artyom's summer work in
// vendor/compliance-dashboard (utils/chartPalette.js, components/DesignPanel.jsx designToCssVars,
// tokens/dashboardTokens.js widget.title, ds WidgetCard / WidgetHeader).
//
// It reads the design keys that Bobur's Brand panel already persists in config.design:
//   accent, headerColor, radius ("square" | "medium" | "large"), fontSize ("compact" | "normal" | "large"),
//   headingFont, bodyFont, widgetShadows, lightGrayBackground.
// The one summer key the Brand panel has no control for is the widget title colour; it is read
// as `titleColor` when present (headerColor stays the header band colour, as Bobur uses it).

export const DEFAULT_ACCENT = "#2f80ed"

// ── Chart palette (summer utils/chartPalette.js) ───────────────────────────────────────────
export const CHART_COLORS = [
  "#1e88e5", // blue
  "#1f2fa3", // dark blue
  "#e87434", // orange
  "#8e33a8", // purple
  "#d84ba8", // pink
  "#1db13b", // green
  "#8fbce6", // light blue
  "#0f9d8f", // teal
  "#c79a1e", // amber
  "#b8453d", // brick red
]

export const CHART_SEMANTIC = { positive: "#1db13b", negative: "#d8625a", benchmark: "#caa437" }

export const CHART_CHROME = { axisText: "#667085", gridLine: "#e4e7ec", labelText: "#344054", valueText: "#1f2937" }

const PALETTE_SIZE = 10

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

function hexToHsl(hex) {
  const match = /^#?([\da-f]{6})$/i.exec(String(hex || "").trim())
  if (!match) return null
  const int = parseInt(match[1], 16)
  const r = ((int >> 16) & 255) / 255
  const g = ((int >> 8) & 255) / 255
  const b = (int & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  let h = 0
  let s = 0
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1))
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return { h, s, l }
}

function hslToHex(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
  const to = (value) =>
    Math.round((value + m) * 255)
      .toString(16)
      .padStart(2, "0")
  return `#${to(r)}${to(g)}${to(b)}`
}

// Summer getChartPalette: the accent stays exact as the lead colour, the rest sit at evenly
// spaced hues with a muted saturation and lightness, so they read as one family.
export function accentChartPalette(accent) {
  const base = hexToHsl(accent)
  if (!base) return CHART_COLORS
  const s = clamp(base.s * 0.7, 0.32, 0.5)
  const l = clamp(base.l, 0.5, 0.62)
  const colors = [String(accent).trim()]
  for (let index = 1; index < PALETTE_SIZE; index += 1)
    colors.push(hslToHex((base.h + (360 / PALETTE_SIZE) * index) % 360, s, l))
  return colors
}

// The brand ramp unless the design (or the widget) sets its own accent; the default accent
// counts as "not set", so untouched dashboards get the summer brand ramp.
export function chartPalette(accent) {
  if (!hexToHsl(accent) || String(accent).trim().toLowerCase() === DEFAULT_ACCENT) return CHART_COLORS
  return accentChartPalette(accent)
}

// Series that the agent names as a benchmark or index get the benchmark colour.
export function isBenchmarkSeries(name) {
  return /\b(benchmark|bmk|index)\b/i.test(String(name || ""))
}

// ── Design → CSS custom properties (summer designToCssVars) ────────────────────────────────
// The sheet's style (V2 only, DESIGN-LOG §75): one look for every widget of the report, chosen in the Brand panel and
// kept in config.design.sheetStyle. The renderer marks the page and each widget with it (data-sheet-style); the V2
// stylesheets (datox/v2/sheet/*.css) draw each one, so the flag-off app keeps Bobur's look. Unknown or unset: Lesha, the
// bank's own (Artyom: «стиль леиша должен быть дефолтом»), first in the Brand panel.
export const SHEET_STYLES = ["lesha", "swiss", "cards", "bold"]
export function sheetStyle(design) {
  return SHEET_STYLES.includes(design?.sheetStyle) ? design.sheetStyle : "lesha"
}
// `color` laid over white at `amount` (0–1), as a hex: a tint the PDF renderer (html2canvas 1.4.1) can read, which
// color-mix() is not. Unknown colours give white.
export function tintHex(color, amount) {
  const match = /^#?([\da-f]{6})$/i.exec(String(color || "").trim())
  if (!match) return "#ffffff"
  const value = parseInt(match[1], 16)
  const channel = (shift) => Math.round(255 - (255 - ((value >> shift) & 255)) * amount)
  return `#${[16, 8, 0].map((shift) => channel(shift).toString(16).padStart(2, "0")).join("")}`
}
export const HEADER_TINT = 0.06

// Whether the header band is dark (light text on it) or light: the styles that draw in its colour (Swiss's rules,
// Bold's fills) read it as data-header-tone, so a white header from Brand never leaves an invisible rule.
export function headerTone(design) {
  return readableTextOn(design?.headerColor || DEFAULT_TITLE_COLOR) === TEXT_ON_DARK ? "dark" : "light"
}

export const RADIUS_PX = { square: 0, medium: 8, large: 12 }
export const FONT_SCALE = { compact: 0.92, normal: 1, large: 1.12 }
export const WIDGET_SHADOW = "0 1px 2px rgba(16, 24, 40, 0.05), 0 6px 16px rgba(16, 24, 40, 0.08)"
export const DEFAULT_TITLE_COLOR = "#101828"

const FONT_STACKS = {
  Inter: "Inter, ui-sans-serif, system-ui, sans-serif",
  Roboto: "Roboto, ui-sans-serif, system-ui, sans-serif",
  Arial: "Arial, sans-serif",
  Verdana: "Verdana, sans-serif",
  Serif: "Georgia, serif",
  Georgia: "Georgia, serif",
}

// Bobur's font names (Brand panel). "System" and unset keep the island's Inter default.
export function designFontStack(value) {
  return FONT_STACKS[value] || FONT_STACKS.Inter
}

export function designFontScale(design) {
  return FONT_SCALE[design?.fontSize] || 1
}

export function designRadius(design, widgetStyle) {
  const setting = widgetStyle?.radius || design?.radius || "medium"
  return RADIUS_PX[setting] ?? RADIUS_PX.medium
}

// ── Readable text on a coloured surface ──────────────────────────────────────────────────────
// Summer: the header band flips to dark text on a light colour (DashboardWorkspaceWidget
// isLightColor -> #1d2939 / #ffffff), and a filled table header picks its text by WCAG contrast
// (utils/tableHeaderColor.js headerTextColor). One rule here: the candidate with the higher WCAG
// contrast wins. Non-hex colours count as dark (white text), as in summer. Logos are never recoloured.
export const TEXT_ON_LIGHT = "#1d2939"
export const TEXT_ON_DARK = "#ffffff"

function hexChannels(color) {
  const match = /^#?([\da-f]{3}|[\da-f]{6})$/i.exec(String(color || "").trim())
  if (!match) return null
  const hex = match[1].length === 3 ? [...match[1]].map((digit) => digit + digit).join("") : match[1]
  return [0, 2, 4].map((start) => parseInt(hex.slice(start, start + 2), 16) / 255)
}

export function relativeLuminance(color) {
  const channels = hexChannels(color)
  if (!channels) return null
  const [red, green, blue] = channels.map((channel) =>
    channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  )
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

export function contrastRatio(first, second) {
  const a = relativeLuminance(first)
  const b = relativeLuminance(second)
  if (a == null || b == null) return null
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

// ── Logo on the header band ──────────────────────────────────────────────────────────────────
// Logos are never recoloured. A logo whose ink is too close to the header colour (a navy Lesha
// mark on the dark band) sits on a light plate instead, by the same WCAG rule as the header text.

/**
 * Median relative luminance of the visible pixels of RGBA image data (alpha over 50%), or null
 * when the image has none. The median, not the mean: the Lesha logo is mostly a navy mark with a
 * light word under it, and its mean (0.15) passes 3:1 on the dark band while the mark vanishes.
 * @param {ArrayLike<number>} data
 */
export function imageLuminance(data) {
  const values = []
  for (let index = 0; index + 3 < data.length; index += 4) {
    if (data[index + 3] < 128) continue
    const [red, green, blue] = [data[index], data[index + 1], data[index + 2]].map((value) => {
      const channel = value / 255
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    })
    values.push(0.2126 * red + 0.7152 * green + 0.0722 * blue)
  }
  if (!values.length) return null
  values.sort((first, second) => first - second)
  const middle = values.length >> 1
  return values.length % 2 ? values[middle] : (values[middle - 1] + values[middle]) / 2
}

export const LOGO_PLATE_MIN_CONTRAST = 3

/**
 * True when the logo needs the light plate: the header is dark (it takes white text) and the
 * logo's luminance gives less than 3:1 against it. An unknown logo luminance (the image could not
 * be read) keeps the logo as it is.
 * @param {string | undefined} headerColor @param {number | null | undefined} logoLuminance
 */
export function logoNeedsPlate(headerColor, logoLuminance) {
  if (logoLuminance == null || !Number.isFinite(logoLuminance)) return false
  const header = relativeLuminance(headerColor || DEFAULT_TITLE_COLOR)
  if (header == null || readableTextOn(headerColor || DEFAULT_TITLE_COLOR) !== TEXT_ON_DARK) return false
  const contrast = (Math.max(header, logoLuminance) + 0.05) / (Math.min(header, logoLuminance) + 0.05)
  return contrast < LOGO_PLATE_MIN_CONTRAST
}

/** @param {string | undefined} background @param {{ dark?: string, light?: string }} [options] */
export function readableTextOn(background, options = {}) {
  const dark = options.dark || TEXT_ON_LIGHT
  const light = options.light || TEXT_ON_DARK
  if (relativeLuminance(background) == null) return light
  return (contrastRatio(background, dark) ?? 0) > (contrastRatio(background, light) ?? 0) ? dark : light
}

/**
 * @param {Record<string, any> | undefined} design config.design
 * @param {{ accent?: string, radius?: string }} [widgetStyle] per-widget overrides
 */
export function designToCssVars(design = {}, widgetStyle) {
  const radius = designRadius(design, widgetStyle)
  const scale = designFontScale(design)
  return {
    "--ds-accent": widgetStyle?.accent || design?.accent || DEFAULT_ACCENT,
    "--ds-header-color": design?.headerColor || DEFAULT_TITLE_COLOR,
    "--ds-header-text": readableTextOn(design?.headerColor || DEFAULT_TITLE_COLOR),
    "--ds-accent-text": readableTextOn(widgetStyle?.accent || design?.accent || DEFAULT_ACCENT),
    "--ds-title-color": design?.titleColor || DEFAULT_TITLE_COLOR,
    // §75: a rule drawn in the header's colour, or in the title ink when the header is too light to show one.
    // §75: the header colour at 6% in white (a table band in Cards).
    "--ds-header-tint": tintHex(design?.headerColor || DEFAULT_TITLE_COLOR, HEADER_TINT),
    "--ds-rule-color": headerTone(design) === "dark" ? design?.headerColor || DEFAULT_TITLE_COLOR : design?.titleColor || DEFAULT_TITLE_COLOR,
    // The readable text on that colour (Bold fills its KPI tiles and table bands with it).
    "--ds-rule-text": readableTextOn(headerTone(design) === "dark" ? design?.headerColor || DEFAULT_TITLE_COLOR : design?.titleColor || DEFAULT_TITLE_COLOR),
    "--ds-heading-font": designFontStack(design?.headingFont),
    "--ds-body-font": designFontStack(design?.bodyFont),
    "--ds-font-scale": String(scale),
    "--ds-radius-card": `${radius}px`,
    "--ds-widget-shadow": design?.widgetShadows ? WIDGET_SHADOW : "none",
    "--ds-canvas-bg": design?.lightGrayBackground ? "#f3f4f6" : "#ffffff",
  }
}

// ── Titles (summer widget.title levels and WidgetHeader) ───────────────────────────────────
export const TITLE_LEVELS = { h1: 40, h2: 34, h3: 28, h4: 22 }
export const TITLE_WEIGHT = 800

// A title widget picks the largest summer level whose two lines fit its height.
export function titleWidgetSize(height, fontScale = 1) {
  const available = Math.max(0, Number(height || 0) - 28)
  const levels = [TITLE_LEVELS.h1, TITLE_LEVELS.h2, TITLE_LEVELS.h3, TITLE_LEVELS.h4]
  const size = levels.find((level) => level * 1.15 * 1.5 <= available) ?? TITLE_LEVELS.h4
  return Math.round(size * (Number(fontScale) || 1))
}

// Widget header titles: summer WidgetHeader uses --ds-text-md (14px, scaled by the Design font
// size) at every widget size. Width and height are kept in the signature for callers.
export const WIDGET_TITLE_PX = 14
export function widgetTitleSize(width, height, fontScale = 1) {
  return Math.round(WIDGET_TITLE_PX * (Number(fontScale) || 1))
}

// ── Smart titles (display only) ────────────────────────────────────────────────────────────
// The report agent writes full sentences as widget titles. The summer rule is a short name in
// the title line, with the detail in a muted line under it. This never rewrites the saved title.

const MONTHS =
  "January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec"
const PERIOD_PREFIX = new RegExp(
  `^((?:Q[1-4]\\s+)?(?:(?:${MONTHS})\\s+)?(?:19|20)\\d{2}(?:\\s+(?:month|quarter|year)-to-date|\\s+(?:MTD|QTD|YTD))?)\\s+(.+)$`,
  "i",
)
const TO_DATE_PREFIX = /^((?:month|quarter|year)-to-date|MTD|QTD|YTD)\s+(.+)$/i
const UNIT = /^(?:%|[A-Z]{3}|bps|bp|x|pp|base \d+)$/i
const TITLE_LIMIT = 40

// Split on ";" outside parentheses only, so "(%; 92.8% eligible)" stays one part.
function splitTopLevel(text) {
  const parts = [""]
  let depth = 0
  for (const char of text) {
    if (char === "(") depth += 1
    if (char === ")") depth = Math.max(0, depth - 1)
    if (char === ";" && depth === 0) parts.push("")
    else parts[parts.length - 1] += char
  }
  return parts.map((part) => part.trim()).filter(Boolean)
}

function upperFirst(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
}

function tidyDetail(text) {
  const value = text.trim()
  const range = /^(\S+)\s+to\s+(\S+)$/i.exec(value)
  if (range) return range[1] === range[2] ? range[1] : `${range[1]} – ${range[2]}`
  return value
}

/**
 * Split an agent title into a short title and a muted subtitle.
 * - a ";" ends the title; what follows goes to the subtitle;
 * - a trailing "(…)" keeps a unit such as "%", "QAR" or "base 100" in the title and moves
 *   the rest (dates, coverage) to the subtitle; a short plain parenthetical stays as it is;
 * - a leading period ("April 2026 month-to-date …") moves to the subtitle;
 * - "X to X" date ranges collapse to one date; a trailing full stop is dropped.
 * @param {unknown} raw
 * @returns {{ title: string, subtitle: string, full: string }}
 */
export function splitWidgetTitle(raw) {
  const full = String(raw ?? "")
    .replace(/\s+/g, " ")
    .trim()
  if (!full) return { title: "", subtitle: "", full }
  const details = []
  let [title = full, ...rest] = splitTopLevel(full)
  title = title.replace(/\.$/, "")
  const paren = /^(.*\S)\s*\(([^()]*)\)$/.exec(title)
  if (paren) {
    const parts = paren[2]
      .split(/\s*;\s*/)
      .map((part) => part.trim())
      .filter(Boolean)
    const keep = parts.length && UNIT.test(parts[0]) ? parts.shift() : ""
    if (!parts.length && (keep || title.length <= TITLE_LIMIT)) {
      title = keep ? `${paren[1]} (${keep})` : title
    } else {
      title = keep ? `${paren[1]} (${keep})` : paren[1]
      details.push(...parts)
    }
  }
  const period = PERIOD_PREFIX.exec(title) || TO_DATE_PREFIX.exec(title)
  if (period) {
    details.unshift(period[1])
    title = upperFirst(period[2])
  }
  details.push(...rest.map((part) => part.replace(/\.$/, "")))
  const subtitle = upperFirst(details.map(tidyDetail).filter(Boolean).join(" · "))
  return { title, subtitle, full }
}
